import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, AlertCircle, Upload, Send, Lock, RefreshCw, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import useAuthStore from '../lib/auth-store';
import { apiFetch, fmtDate, isAdmin, roleLabel } from '../components/merchant/attributionApi';

const API = process.env.REACT_APP_BACKEND_URL;
const METHODS = [['api', 'Processor API'], ['e_signature', 'E-signature'], ['pdf', 'Generated PDF'], ['manual', 'Manual package']];

function Pick({ value, onChange, options, placeholder, testId }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function Preflight({ app, token, groupId, reload }) {
  const pf = app.preflight;
  const [docType, setDocType] = useState('');
  const fileRef = useRef(null);
  const upload = async (file) => {
    const fd = new FormData();
    fd.append('file', file); fd.append('document_type', docType);
    const res = await fetch(`${API}/api/merchants/groups/${groupId}/documents`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    if (res.ok) { toast.success('Document added to the Merchant Vault'); reload(); } else toast.error('Upload failed');
  };
  return (
    <Card className="p-5 space-y-4" data-testid="preflight-card">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Preflight</h3>
        <Badge className={pf.overall_status === 'ready' ? 'bg-emerald-600' : 'bg-amber-500'} data-testid="preflight-overall">{pf.overall_status === 'ready' ? 'READY' : 'MISSING INFORMATION'}</Badge>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {Object.entries(pf.sections).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-sm" data-testid={`preflight-section-${k}`}>
            {v === 'complete' ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-amber-500" />}
            <span className="capitalize">{k}</span><span className="text-xs text-muted-foreground">{v === 'complete' ? 'Complete' : 'Missing'}</span>
          </div>
        ))}
      </div>
      {pf.missing.length > 0 && (
        <div data-testid="preflight-missing">
          <p className="text-xs font-medium mb-1">Missing:</p>
          <ul className="text-sm list-disc pl-5 space-y-0.5">{pf.missing.map((m) => <li key={m.field}>{m.label}</li>)}</ul>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t">
        <div className="sm:w-64"><Pick value={docType} onChange={setDocType} placeholder="Document type" testId="preflight-doc-type" options={app.document_types.map((d) => [d.id, d.label])} /></div>
        <input ref={fileRef} type="file" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="preflight-doc-file" />
        <Button variant="outline" disabled={!docType} onClick={() => fileRef.current?.click()} data-testid="preflight-doc-upload"><Upload className="w-4 h-4 mr-1" /> Upload</Button>
      </div>
    </Card>
  );
}

function SubmitCard({ app, token, admin, reload }) {
  const [method, setMethod] = useState(app.template?.submission_method || 'api');
  const [override, setOverride] = useState('');
  const [live, setLive] = useState(false);
  const [busy, setBusy] = useState(false);
  const ready = app.preflight?.overall_status === 'ready';
  const isLive = app.connection?.environment === 'production' && method === 'api';
  const go = async () => {
    setBusy(true);
    try {
      const r = await apiFetch(token, `/boarding/applications/${app.id}/submit`, 'POST', { method, override_reason: override || undefined, confirm_live: live });
      toast.success(`Submitted (${roleLabel(r.status)}) — snapshot frozen`); reload();
    } catch (e) { toast.error(e.message === 'Request failed' ? 'Preflight is not complete' : e.message); } finally { setBusy(false); }
  };
  return (
    <Card className="p-5 space-y-3" data-testid="submit-card">
      <h3 className="font-semibold text-sm">Submit</h3>
      <Pick value={method} onChange={setMethod} testId="submit-method" options={METHODS} />
      {!ready && admin && <Input placeholder="Admin override reason (audited)" value={override} onChange={(e) => setOverride(e.target.value)} data-testid="submit-override-reason" />}
      {!ready && !admin && <p className="text-xs text-muted-foreground">Complete the missing items above, or ask an ISO admin to override.</p>}
      {isLive && (
        <label className="flex items-start gap-2 text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded p-2">
          <Checkbox checked={live} onCheckedChange={(v) => setLive(!!v)} data-testid="submit-confirm-live" /> This is a LIVE processor connection. Send a real application.
        </label>
      )}
      <Button className="w-full" onClick={go} disabled={busy || (!ready && !override) || (isLive && !live)} data-testid="submit-btn"><Send className="w-4 h-4 mr-1" /> {busy ? 'Freezing snapshot…' : 'Freeze snapshot & submit'}</Button>
    </Card>
  );
}

function DecisionCard({ app, token, reload }) {
  const [f, setF] = useState({ canonical_status: '', external_status: '', mid: '' });
  const save = async () => {
    try {
      await apiFetch(token, `/boarding/applications/${app.id}/decision`, 'POST', Object.fromEntries(Object.entries(f).filter(([, v]) => v)));
      toast.success('Decision recorded'); setF({ canonical_status: '', external_status: '', mid: '' }); reload();
    } catch (e) { toast.error(e.message); }
  };
  const sync = async () => {
    try { const r = await apiFetch(token, `/boarding/applications/${app.id}/sync`, 'POST'); toast.success(r.changed ? `Status: ${roleLabel(r.status)}` : 'No change at the processor'); reload(); } catch (e) { toast.error(e.message); }
  };
  return (
    <Card className="p-5 space-y-3" data-testid="decision-card">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Processor decision</h3>
        {app.external_id && <Button size="sm" variant="ghost" onClick={sync} data-testid="decision-sync"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Sync</Button>}
      </div>
      <Pick value={f.canonical_status} onChange={(v) => setF({ ...f, canonical_status: v })} placeholder="ISOCNTRL status" testId="decision-status" options={app.statuses.map((s) => [s, roleLabel(s)])} />
      <Input placeholder="…or vendor status (mapped automatically)" value={f.external_status} onChange={(e) => setF({ ...f, external_status: e.target.value })} data-testid="decision-external-status" />
      <Input placeholder="Returned MID (required for approval)" value={f.mid} onChange={(e) => setF({ ...f, mid: e.target.value })} data-testid="decision-mid" />
      <Button className="w-full" onClick={save} disabled={!f.canonical_status && !f.external_status} data-testid="decision-save">Record decision</Button>
    </Card>
  );
}

export default function BoardingApplicationPage() {
  const { appId } = useParams();
  const { token, user } = useAuthStore();
  const [app, setApp] = useState(null);
  const load = useCallback(() => apiFetch(token, `/boarding/applications/${appId}`).then(setApp).catch((e) => toast.error(e.message)), [token, appId]);
  useEffect(() => { load(); }, [load]);
  if (!app) return <div className="py-16 text-center text-muted-foreground">Loading…</div>;
  const admin = isAdmin(user?.role);
  const submitted = !!app.submission;
  return (
    <div className="space-y-6" data-testid="boarding-application-page">
      <Link to={`/merchants/${app.merchant_group_id}`} className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Merchant profile</Link>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" data-testid="boarding-title">{app.business_name} · {(app.platform || '').toUpperCase()}</h1>
          <p className="text-sm text-muted-foreground">{app.template?.vendor_name} template v{app.template?.version} · {app.connection?.label}</p>
        </div>
        <Badge variant="outline" className="text-sm w-fit" data-testid="boarding-status">{roleLabel(app.boarding_status)}</Badge>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {!submitted && app.preflight && <Preflight app={app} token={token} groupId={app.merchant_group_id} reload={load} />}
          {submitted && (
            <Card className="p-5 space-y-2" data-testid="submission-card">
              <h3 className="font-semibold text-sm flex items-center gap-2"><Lock className="w-4 h-4" /> Submitted {fmtDate(app.submission.at)} via {Object.fromEntries(METHODS)[app.submission.method] || app.submission.method}</h3>
              <p className="text-xs text-muted-foreground">The application snapshot is frozen. Later vault changes never rewrite it.</p>
              {app.external_id && <p className="text-xs">Provider reference: {app.external_id} · {app.external_status}</p>}
              {app.preflight_override && <p className="text-xs text-amber-600" data-testid="submission-override">Preflight overridden: {app.preflight_override.reason}</p>}
            </Card>
          )}
          {app.processing_account && (
            <Card className="p-5 flex items-center gap-3" data-testid="boarded-processing-account">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              <div className="text-sm"><b>{app.processing_account.processor?.toUpperCase()}</b> MID {app.processing_account.mid} · {app.processing_account.status} — attached to this location</div>
            </Card>
          )}
          <Card className="p-5" data-testid="prefill-card">
            <h3 className="font-semibold text-sm mb-3">Prefilled from the Merchant Vault</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              {Object.entries(app.prefill).map(([k, v]) => <div key={k} className="flex justify-between gap-2 border-b border-border/50 py-1"><span className="text-muted-foreground text-xs">{k}</span><span className="truncate">{String(v ?? '—') || '—'}</span></div>)}
            </div>
          </Card>
        </div>
        <div className="space-y-4">
          {!submitted ? <SubmitCard app={app} token={token} admin={admin} reload={load} /> : admin && <DecisionCard app={app} token={token} reload={load} />}
          <Card className="p-5" data-testid="boarding-history">
            <h3 className="font-semibold text-sm mb-2">Status history</h3>
            {app.boarding_history.map((h, i) => <div key={i} className="text-xs flex justify-between py-1 border-b last:border-0"><span>{roleLabel(h.status)}</span><span className="text-muted-foreground">{fmtDate(h.at)}</span></div>)}
            {app.snapshots?.length > 0 && <p className="text-xs text-muted-foreground mt-2">{app.snapshots.length} frozen snapshot(s)</p>}
          </Card>
        </div>
      </div>
    </div>
  );
}
